import { useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchPattern } from '@/lib/api';

interface PatternThumbnailProps {
  readonly slug: string;
  readonly width: number;
  readonly height: number;
}

/**
 * Renders a small canvas thumbnail of a pattern by fetching its grid data.
 * Uses a shared query cache so each pattern is fetched only once.
 */
export function PatternThumbnail({ slug, width, height }: PatternThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const { data } = useQuery({
    queryKey: ['pattern-thumb', slug],
    queryFn: () => fetchPattern(slug),
    staleTime: Infinity,
  });

  useEffect(() => {
    const canvas = canvasRef.current;
    const patternData = data?.data;
    if (!canvas || !patternData) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { gridData } = patternData;
    const gridW = patternData.width;
    const gridH = patternData.height;

    // Calculate cell size to fit in canvas
    const cellW = width / gridW;
    const cellH = height / gridH;
    const cellSize = Math.min(cellW, cellH);

    // Center the grid
    const offsetX = (width - gridW * cellSize) / 2;
    const offsetY = (height - gridH * cellSize) / 2;

    // Clear
    ctx.clearRect(0, 0, width, height);

    // Draw beads
    for (let r = 0; r < gridH; r++) {
      const row = gridData.grid[r];
      if (!row) continue;
      for (let c = 0; c < gridW; c++) {
        const val = row[c];
        if (val === undefined || val === 0) continue;

        const color = gridData.palette[val - 1];
        if (!color) continue;

        ctx.fillStyle = color.hex;
        ctx.fillRect(
          offsetX + c * cellSize,
          offsetY + r * cellSize,
          Math.max(cellSize - 0.5, 1),
          Math.max(cellSize - 0.5, 1),
        );
      }
    }
  }, [data, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      className="h-full w-full"
    />
  );
}
